/** @jsx jsx */
import { defineMessages, FormattedMessage } from 'react-intl';
import { css, jsx } from '@compiled/react';
import { useCallback } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Flex from '@atlaskit/primitives/components/flex';
import Box from '@atlaskit/primitives/box';
import Text from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss';
import Image from '@atlaskit/image';
import Heading from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';

import { HTV2DismissButton } from './HTV2DismissButton.tsx';
import type { OnboardingAction } from './cards/HTV2CardTypes.ts';
import { useHTV2Actions } from './useHTV2Store.ts';

const contentContainerStyles = xcss({
	paddingBlock: 'space.200',
	paddingInline: 'space.200',
	height: '100%',
});

const cardStyles = xcss({
	overflow: 'hidden',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: 'radius.large',
	background: token('elevation.surface'),
});

const fadeStyles = xcss({
	opacity: '0.3',
});

const imageContainerStyles = xcss({
	position: 'relative',
});

const imageStyles = css({ display: 'block', width: '100%' });

const descriptionContainerStyles = xcss({
	// Ensure the description text is always 3 lines tall
	height: '4.28em', // (20px line height * 3 lines) / 14px font size
});

const i18n = defineMessages({
	dismissButtonLabel: {
		id: 'experiment-home-onboarding-takeover-v2.dismiss-card-button-label',
		defaultMessage: 'Dismiss this action',
		description:
			'This text is shown as a label for a dismiss/cross icon for accessibility. The icon can then be used to dismiss the card. The placeholder {title} is replaced with the title of the card.',
	},
	completedCardText: {
		id: 'experiment-home-onboarding-takeover-v2.completed-card-text',
		defaultMessage: 'You completed this step.',
		description: 'This text is shown on cards when the user has completed the associated action.',
	},
});

export const HTV2ActionCard = ({
	action,
	dismiss,
	complete,
	onboardingPeriodDay,
	fadeCardOnComplete = false,
	keepCtaOnComplete = false,
}: {
	action: OnboardingAction;
	dismiss?: (id: OnboardingAction['id']) => void;
	complete?: (id: OnboardingAction['id']) => void;
	onboardingPeriodDay?: number;
	fadeCardOnComplete?: boolean;
	keepCtaOnComplete?: boolean;
}): JSX.Element => {
	const Cta = action.cta;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { getIsXflowTakeoverType } = useHTV2Actions();
	const isXflow = getIsXflowTakeoverType();
	const handleClick = useCallback(() => {
		complete?.(action.id);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2ActionCta',
				attributes: {
					onboardingAction: action.id,
					onboardingPeriodDay,
					isXflow,
				},
				source: 'onboarding',
			},
		}).fire();
	}, [complete, createAnalyticsEvent, action.id, onboardingPeriodDay, isXflow]);

	const handleDismiss = useCallback(() => {
		if (dismiss) {
			dismiss(action.id);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2ActionDismiss',
				attributes: {
					onboardingAction: action.id,
					onboardingPeriodDay,
					isXflow,
				},
				source: 'onboarding',
			},
		}).fire();
	}, [dismiss, createAnalyticsEvent, action.id, onboardingPeriodDay, isXflow]);

	const isCompleted = action.state === 'completed';

	return (
		<Flex
			testId={isCompleted ? 'htv2-onboarding-action-completed' : 'htv2-onboarding-action-pending'}
			direction="column"
			xcss={cardStyles}
		>
			<Box xcss={[imageContainerStyles, fadeCardOnComplete && isCompleted && fadeStyles]}>
				<Image css={imageStyles} src={action.image.light} srcDark={action.image.dark} alt="" />
				{dismiss && (
					<HTV2DismissButton label={{ ...i18n.dismissButtonLabel }} onDismiss={handleDismiss} />
				)}
			</Box>
			<Flex
				xcss={contentContainerStyles}
				justifyContent="space-between"
				direction="column"
				gap="space.100"
			>
				<Flex direction="column" gap="space.100">
					<Flex direction="row" gap="space.100">
						<Heading size="xsmall">{action.title}</Heading>
						{isCompleted && <StatusSuccessIcon color="var(--ds-icon-success)" label="completed" />}
					</Flex>
					<Box xcss={descriptionContainerStyles}>
						<Text color={isCompleted ? 'color.text.subtlest' : undefined} maxLines={3}>
							<FormattedMessage {...(isCompleted ? i18n.completedCardText : action.description)} />
						</Text>
					</Box>
				</Flex>
				{(!isCompleted || keepCtaOnComplete) && (
					<Flex justifyContent="end">
						<Cta onClick={handleClick} />
					</Flex>
				)}
			</Flex>
		</Flex>
	);
};
